/* Contexte global "jobs d'import groupé de copies actifs" — monté sur
   rootRoute (voir router.tsx), au même niveau que ExamImportJobsProvider.
   Même mécanisme exact que use-exam-import-jobs.tsx (spec 13) : continue
   tant que l'app est montée, peu importe la route affichée, reprend l'état
   réel depuis le serveur au montage. Contexte séparé, pas une extension du
   contexte d'import d'examen — keyé par sessionId (pas documentId, un même
   document peut être rattaché à plusieurs sessions) et porte les champs
   propres à ce jobKind (pagesTotal/copiesPersisted) — GO Ismaël 09/10. */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { api } from '../lib/api';
import { useAuth } from './use-auth';

export interface TrackedBulkImportJob {
  readonly id: string;
  readonly sessionId: string;
  readonly status: 'running' | 'succeeded' | 'failed';
  readonly step: 'generating' | 'saving' | 'done';
  readonly pagesTotal: number;
  readonly copiesPersisted: number;
  readonly error: string | null;
  readonly failureKind: string | null;
}

export interface BulkImportNotice {
  readonly sessionId: string;
  readonly status: 'succeeded' | 'failed';
  readonly error: string | null;
  readonly failureKind: string | null;
}

interface RawJobRow {
  id: string;
  status: 'running' | 'succeeded' | 'failed';
  step: 'generating' | 'saving' | 'done';
  pages_total: number;
  copies_persisted: number;
  error: string | null;
  failure_kind: string | null;
}

interface Entry {
  job: TrackedBulkImportJob;
  /** Une UI mountée (la modale d'import) suit déjà ce job elle-même — pas de
   *  notification globale à sa fin, elle l'affiche elle-même. Sans porteur
   *  (navigation, refresh, onglet fermé puis rouvert) : la barre + le toast
   *  globaux en deviennent responsables. */
  claimed: boolean;
}

interface BulkImportJobsState {
  jobs: readonly TrackedBulkImportJob[];
  notice: BulkImportNotice | null;
  dismissNotice: () => void;
  /** Commence (ou reprend) le suivi d'une session — appelé par la modale dès
   *  qu'elle connaît le sessionId d'un job qu'elle vient de démarrer. */
  trackJob: (sessionId: string, opts?: { claim?: boolean }) => void;
  /** La modale ne suit plus cette session (fermeture, unmount) — s'il tourne
   *  encore, la barre globale en reprend le suivi ; s'il est déjà terminé,
   *  on suppose qu'elle a déjà montré le résultat, pas de notification. */
  releaseJob: (sessionId: string) => void;
  getJob: (sessionId: string) => TrackedBulkImportJob | undefined;
}

const BulkImportJobsContext = createContext<BulkImportJobsState | null>(null);

const POLL_MS = 2_000;
const hintKey = (uid: string) => `naia.bulkImport.activeSession.${uid}`;

const fromRawRow = (sessionId: string, row: RawJobRow): TrackedBulkImportJob => ({
  id: row.id,
  sessionId,
  status: row.status,
  step: row.step,
  pagesTotal: row.pages_total,
  copiesPersisted: row.copies_persisted,
  error: row.error,
  failureKind: row.failure_kind,
});

export function BulkImportJobsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const [entries, setEntries] = useState<Map<string, Entry>>(new Map());
  const [notice, setNotice] = useState<BulkImportNotice | null>(null);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const persistHint = useCallback((id: string, sessionId: string | null) => {
    try {
      if (sessionId) localStorage.setItem(hintKey(id), sessionId);
      else localStorage.removeItem(hintKey(id));
    } catch { /* localStorage indisponible (navigation privée…) : pas de source de vérité, tant pis */ }
  }, []);

  /* Reconstruction au montage — jamais localStorage comme vérité, juste un
     indice pour éviter un flash "rien en cours" avant la réponse serveur. */
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    let hinted: string | null = null;
    try { hinted = localStorage.getItem(hintKey(uid)); } catch { /* ignore */ }
    if (hinted) {
      setEntries((prev) => {
        if (prev.has(hinted!)) return prev;
        const next = new Map(prev);
        next.set(hinted!, {
          claimed: false,
          job: {
            id: 'pending', sessionId: hinted!, status: 'running', step: 'generating',
            pagesTotal: 0, copiesPersisted: 0, error: null, failureKind: null,
          },
        });
        return next;
      });
    }

    (async () => {
      const active = (await api.exam.listActiveBulkImportJobs
        .query()
        .catch(() => [] as (RawJobRow & { exam_session_id: string })[])) as (RawJobRow & { exam_session_id: string })[];
      if (cancelled) return;

      if (active.length > 0) {
        setEntries((prev) => {
          const next = new Map(prev);
          for (const row of active) next.set(row.exam_session_id, { claimed: false, job: fromRawRow(row.exam_session_id, row) });
          return next;
        });
        persistHint(uid, active[0]!.exam_session_id);
        return;
      }

      /* Rien en cours côté serveur : si l'indice local pointait vers un job
         qui s'est terminé pendant l'absence, on va chercher son résultat
         final pour notifier directement, plutôt qu'une barre à 100% qui ne
         mène nulle part. */
      if (hinted) {
        const job = await api.exam.bulkImportJob.query({ sessionId: hinted }).catch(() => null) as RawJobRow | null;
        setEntries((prev) => {
          const next = new Map(prev);
          next.delete(hinted!);
          return next;
        });
        if (job && job.status !== 'running') {
          setNotice({ sessionId: hinted, status: job.status, error: job.error, failureKind: job.failure_kind });
        }
        persistHint(uid, null);
      }
    })();

    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- persistHint est stable (useCallback sans deps) */
  }, [uid]);

  /* Un seul point de sondage, réutilisé par l'intervalle ci-dessous ET par
     trackJob (poll immédiat à l'ajout). */
  const pollSession = useCallback(async (sessionId: string) => {
    const row = (await api.exam.bulkImportJob.query({ sessionId }).catch(() => null)) as RawJobRow | null;
    if (!row) return;
    const job = fromRawRow(sessionId, row);

    /* Même discipline que use-exam-import-jobs.tsx : décision prise DANS
       l'updater sur `prev`, effets de bord déclenchés APRÈS setEntries via
       `result`, jamais dans l'updater lui-même (React peut le réinvoquer). */
    const result: { outcome: 'not-tracked' | 'running' | 'claimed-terminal' | 'unclaimed-terminal' } = { outcome: 'not-tracked' };
    flushSync(() => setEntries((prev) => {
      const e = prev.get(sessionId);
      if (!e) { result.outcome = 'not-tracked'; return prev; }
      if (job.status === 'running') {
        result.outcome = 'running';
        const next = new Map(prev);
        next.set(sessionId, { ...e, job });
        return next;
      }
      if (e.claimed) {
        result.outcome = 'claimed-terminal';
        const next = new Map(prev);
        next.set(sessionId, { ...e, job });
        return next;
      }
      result.outcome = 'unclaimed-terminal';
      const next = new Map(prev);
      next.delete(sessionId);
      return next;
    }));

    if (result.outcome === 'claimed-terminal') {
      if (uid) persistHint(uid, null);
    } else if (result.outcome === 'unclaimed-terminal' && job.status !== 'running') {
      if (uid) {
        setNotice({ sessionId, status: job.status, error: job.error, failureKind: job.failureKind });
        persistHint(uid, null);
      }
    }
  }, [uid, persistHint]);

  /* Sondage — tant qu'au moins un job est suivi, peu importe la route. */
  useEffect(() => {
    if (entries.size === 0) return;
    const timer = window.setInterval(() => {
      for (const [sessionId] of entriesRef.current) void pollSession(sessionId);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [entries.size, pollSession]);

  const trackJob = useCallback((sessionId: string, opts?: { claim?: boolean }) => {
    setEntries((prev) => {
      const existing = prev.get(sessionId);
      const next = new Map(prev);
      next.set(sessionId, {
        claimed: opts?.claim ?? existing?.claimed ?? false,
        job: existing?.job?.status === 'running' ? existing.job : {
          id: 'pending', sessionId, status: 'running', step: 'generating',
          pagesTotal: 0, copiesPersisted: 0, error: null, failureKind: null,
        },
      });
      return next;
    });
    if (uid) persistHint(uid, sessionId);
    void pollSession(sessionId);
  }, [uid, persistHint, pollSession]);

  const releaseJob = useCallback((sessionId: string) => {
    setEntries((prev) => {
      const entry = prev.get(sessionId);
      if (!entry) return prev;
      const next = new Map(prev);
      if (entry.job.status === 'running') {
        next.set(sessionId, { ...entry, claimed: false });
      } else {
        next.delete(sessionId);
        if (uid) persistHint(uid, null);
      }
      return next;
    });
  }, [uid, persistHint]);

  const getJob = useCallback(
    (sessionId: string) => entriesRef.current.get(sessionId)?.job,
    [],
  );

  const dismissNotice = useCallback(() => setNotice(null), []);

  const jobs = useMemo(() => [...entries.values()].map((e) => e.job), [entries]);

  const value = useMemo<BulkImportJobsState>(
    () => ({ jobs, notice, dismissNotice, trackJob, releaseJob, getJob }),
    [jobs, notice, dismissNotice, trackJob, releaseJob, getJob],
  );

  return <BulkImportJobsContext.Provider value={value}>{children}</BulkImportJobsContext.Provider>;
}

export function useBulkImportJobs(): BulkImportJobsState {
  const ctx = useContext(BulkImportJobsContext);
  if (!ctx) throw new Error('useBulkImportJobs must be used within BulkImportJobsProvider');
  return ctx;
}

/** Sélecteur réactif — le job suivi pour une session donnée, s'il existe. */
export function useTrackedBulkImportJob(sessionId: string | null): TrackedBulkImportJob | undefined {
  const { jobs } = useBulkImportJobs();
  return sessionId ? jobs.find((j) => j.sessionId === sessionId) : undefined;
}
